// Retry helper for transient server errors (502, 503, 504, 520, 521, 522, 524)
const RETRYABLE_STATUS_CODES = new Set([502, 503, 504, 520, 521, 522, 524])
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 2000

async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retries = MAX_RETRIES,
): Promise<Response> {
  let lastError: Error | null = null
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options)
      if (RETRYABLE_STATUS_CODES.has(response.status) && attempt < retries) {
        // Wait before retrying on transient server errors
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)))
        continue
      }
      return response
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err))
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)))
        continue
      }
    }
  }
  throw lastError || new Error('Request failed after retries')
}

// methodType: GET, POST, PUT, DELETE
export const callZohoAPI = async (
  zohoApiPath: string,
  methodType?: string,
  postParams?: object,
): Promise<any> => {
  const urlPrefix =
    zohoApiPath.indexOf('http') === 0 ? '' : 'https://www.zohoapis.com/'

  const fullUrl = urlPrefix + zohoApiPath
  const method = methodType ? methodType.toUpperCase() : 'GET'

  const options: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'API-KEY': 'ud7^3jf(80',
    },
    body: JSON.stringify({
      url: fullUrl,
      method: method,
      data: postParams,
    }),
  }
  const zohoResponse = await fetchWithRetry(
    'https://www.websiteforge.com/custom/chosen/chosen_zoho_api.php',
    options,
  )
  if (!zohoResponse.ok) {
    const text = await zohoResponse.text()
    // Strip HTML to get a readable error message
    const cleanText = text
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .substring(0, 500)
    let statusHint = ''
    if (zohoResponse.status === 520) {
      statusHint =
        ' (Cloudflare 520: the API server returned an unexpected error — retried automatically. This is usually temporary; please try again in a moment.)'
    } else if (RETRYABLE_STATUS_CODES.has(zohoResponse.status)) {
      statusHint = ' (server temporarily unavailable — retried automatically)'
    }
    const err = new Error(
      `API ${zohoResponse.status}: ${cleanText || 'Server error'}${statusHint}`,
    )
    // Attach debug info
    ;(err as any).debug = {
      status: zohoResponse.status,
      url: fullUrl,
      method: method,
      payload: postParams,
      rawResponse: text.substring(0, 1000),
    }
    throw err
  }
  if (zohoResponse.status == 204) {
    // empty result
    return null
  }
  let response: any = {}
  const clonedResponse = zohoResponse.clone()
  try {
    response = await zohoResponse.json()
  } catch (e) {
    const text = await clonedResponse.text()
    throw new Error(
      `Non-JSON response (${zohoResponse.status}): ${text.substring(0, 500)}`,
    )
  }

  // ── Unwrap proxy envelope ──
  // The PHP proxy may wrap the Zoho response inside a "result" key
  // (e.g. { result: { code: 3000, data: [...] } }).
  // Detect this and unwrap so downstream code always sees the Zoho payload directly.
  if (
    response.result !== undefined &&
    typeof response.result === 'object' &&
    response.result !== null &&
    !response.data &&
    !response.code
  ) {
    console.log('[callZohoAPI] Unwrapping proxy envelope (response.result)')
    response = response.result
  }

  // Zoho Creator API returns errors with a "code" field (not "error")
  // Success codes: 3000 (success), 3100/9220 (no records)
  if (response.error) {
    const errorDetail =
      typeof response.error === 'object'
        ? JSON.stringify(response.error)
        : response.error
    const err = new Error(`Zoho Error: ${response.message || errorDetail}`)
    ;(err as any).debug = {
      url: fullUrl,
      method,
      payload: postParams,
      response,
    }
    throw err
  }

  // Check for Creator-specific error codes
  if (
    response.code &&
    response.code !== 3000 &&
    response.code !== 3100 &&
    response.code !== 9220
  ) {
    const err = new Error(
      `Creator Error ${response.code}: ${response.message || JSON.stringify(response)}`,
    )
    ;(err as any).debug = {
      url: fullUrl,
      method,
      payload: postParams,
      response,
    }
    throw err
  }

  return response
}

/**
 * Format a Date object into Zoho CRM's expected datetime format.
 * Zoho CRM expects: YYYY-MM-DDTHH:mm:ss±HH:MM (no milliseconds, with timezone offset)
 * e.g., "2026-02-12T13:00:00-05:00"
 *
 * JavaScript's toISOString() produces "2026-02-12T18:00:00.000Z" which Zoho rejects
 * with INVALID_DATA on datetime fields.
 */
export function formatZohoDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())

  const tzOffset = -date.getTimezoneOffset() // in minutes, positive = ahead of UTC
  const tzSign = tzOffset >= 0 ? '+' : '-'
  const tzHours = pad(Math.floor(Math.abs(tzOffset) / 60))
  const tzMinutes = pad(Math.abs(tzOffset) % 60)

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${tzSign}${tzHours}:${tzMinutes}`
}

/**
 * Format a Date object into Zoho Creator's expected datetime format.
 * Zoho Creator expects: dd-MMM-yyyy HH:mm:ss
 * e.g., "12-Feb-2026 13:00:00"
 *
 * This is DIFFERENT from Zoho CRM's format.
 */
export function formatCreatorDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const day = pad(date.getDate())
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`
}

/**
 * Upload a base64-encoded image to the server and return a hosted URL.
 * Endpoint: chosen_image_upload.php
 * Expects JSON body: { image: "<base64>", filename: "<name>" }
 * Returns: { url: "https://..." }
 */
export async function uploadImageToHost(
  base64Data: string,
  filename: string,
): Promise<string> {
  const response = await fetchWithRetry(
    'https://www.websiteforge.com/custom/chosen_image_upload.php',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'API-KEY': 'ud7^3jf(80',
      },
      body: JSON.stringify({ image: base64Data, filename }),
    },
  )
  if (!response.ok) {
    const text = await response.text()
    throw new Error(
      `Image upload failed (${response.status}): ${text.substring(0, 300)}`,
    )
  }
  const result = await response.json()
  if (result.error) {
    throw new Error(result.error)
  }
  if (!result.url) {
    throw new Error('Upload succeeded but no URL was returned')
  }
  return result.url
}

// GPT-specific fetch — the PHP proxy typically times out at ~30-60s,
// so we use a 60s timeout and only retry ONCE on failure (not 3 times).
// This prevents the user from waiting 8+ minutes for inevitable failures.
const GPT_TIMEOUT_MS = 60_000 // 60 seconds (PHP proxy limit)

async function fetchGPTWithRetry(
  url: string,
  options: RequestInit,
): Promise<Response> {
  let lastError: Error | null = null

  // Only 2 attempts total (initial + 1 retry) to avoid long waits
  for (let attempt = 0; attempt < 2; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), GPT_TIMEOUT_MS)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      })
      clearTimeout(timeoutId)

      // Retry once on transient server errors (502, 503, etc.)
      if (RETRYABLE_STATUS_CODES.has(response.status) && attempt === 0) {
        await new Promise((r) => setTimeout(r, 3000))
        continue
      }
      return response
    } catch (err) {
      clearTimeout(timeoutId)
      const isAbort = err instanceof DOMException && err.name === 'AbortError'
      lastError = isAbort
        ? new Error('TIMEOUT')
        : err instanceof Error
          ? err
          : new Error(String(err))

      // On timeout or network error, retry once after a short delay
      if (attempt === 0) {
        await new Promise((r) => setTimeout(r, 2000))
        continue
      }
    }
  }
  throw lastError || new Error('GPT request failed')
}

// Max payload the PHP proxy can handle.
// PHP's default post_max_size is typically 2MB-8MB.
// We use 4MB as a safe limit — well above normal requests but catches truly huge payloads.
const GPT_MAX_PAYLOAD_BYTES = 4 * 1024 * 1024 // 4MB

export const callGPTAPI = async (
  instructions: string,
  prompt: object,
): Promise<string> => {
  const bodyPayload = JSON.stringify({
    instructions: instructions,
    prompt: prompt,
  })

  // Calculate payload size
  const payloadSizeBytes = new Blob([bodyPayload]).size
  const payloadSizeMB = (payloadSizeBytes / (1024 * 1024)).toFixed(2)
  const isLargePayload = payloadSizeBytes > 500 * 1024

  // ── PRE-FLIGHT CHECK: Reject truly oversized payloads immediately ──
  if (payloadSizeBytes > GPT_MAX_PAYLOAD_BYTES) {
    const sizeMBDisplay = (payloadSizeBytes / (1024 * 1024)).toFixed(1)
    throw new Error(
      `Request is too large (${sizeMBDisplay} MB, server limit is ~4 MB). ` +
        `Remove attached files/images and try again.`,
    )
  }

  const options: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'API-KEY': 'ud7^3jf(80',
    },
    body: bodyPayload,
  }

  // Helper to attach debug info to errors so the UI can display it
  const endpoint =
    'https://www.websiteforge.com/custom/chosen/chosen_gpt_api.php'
  const makeDebugError = (
    message: string,
    extra: Record<string, any> = {},
  ): Error => {
    const err = new Error(message)
    ;(err as any).debug = {
      endpoint,
      payloadSizeBytes,
      payloadSizeMB,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      ...extra,
    }
    return err
  }

  let zohoResponse: Response
  try {
    zohoResponse = await fetchGPTWithRetry(endpoint, options)
  } catch (networkErr) {
    const errMsg =
      networkErr instanceof Error ? networkErr.message : 'Unknown network error'

    const isTimeout =
      errMsg === 'TIMEOUT' ||
      errMsg.includes('timed out') ||
      errMsg.includes('abort')

    const message = isTimeout
      ? 'The server took too long to respond (tried twice). Try simplifying your request, or click Retry.'
      : isLargePayload
        ? `Connection failed — payload is ${payloadSizeMB} MB which may exceed the server limit. Try removing attached files/images.`
        : `Connection failed: ${errMsg}. Check your internet and try again.`

    throw makeDebugError(message, {
      errorType: isTimeout ? 'TIMEOUT' : 'NETWORK_ERROR',
      rawError: errMsg,
      httpStatus: null,
      responseBody: null,
    })
  }

  if (!zohoResponse.ok) {
    let responseBody = ''
    let rawResponseBody = ''
    try {
      rawResponseBody = await zohoResponse.text()
      responseBody = rawResponseBody
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 500)
    } catch {
      /* ignore */
    }

    let message: string
    if (
      zohoResponse.status === 413 ||
      (zohoResponse.status >= 500 && isLargePayload)
    ) {
      message = `Request too large (${payloadSizeMB} MB). Try removing large file attachments or using smaller images.`
    } else if (zohoResponse.status === 504 || zohoResponse.status === 408) {
      message =
        'GPT request timed out on the server. Please try again — complex requests sometimes need a second attempt.'
    } else {
      message = `GPT API Error ${zohoResponse.status}: ${responseBody || 'No response body'}`
    }

    throw makeDebugError(message, {
      errorType: 'HTTP_ERROR',
      httpStatus: zohoResponse.status,
      httpStatusText: zohoResponse.statusText,
      responseBody: rawResponseBody.substring(0, 2000),
      responseHeaders: Object.fromEntries(zohoResponse.headers.entries()),
    })
  }

  let response: any = {}
  const clonedResponse = zohoResponse.clone()
  try {
    response = await zohoResponse.json()
  } catch (e) {
    const text = await clonedResponse.text()
    throw makeDebugError(
      `Error parsing GPT response: ${text.substring(0, 300)}`,
      {
        errorType: 'PARSE_ERROR',
        httpStatus: zohoResponse.status,
        responseBody: text.substring(0, 2000),
      },
    )
  }
  if (response.error) {
    const detail =
      response.message ||
      (typeof response.error === 'object'
        ? JSON.stringify(response.error)
        : response.error)
    throw makeDebugError(`GPT Error: ${detail}`, {
      errorType: 'GPT_API_ERROR',
      httpStatus: zohoResponse.status,
      gptError: response.error,
      gptMessage: response.message,
    })
  }
  return response.result
}
